import { ITEM_IDS, count, isItemId, type Inventory, type ItemId } from "@dyson/shared";

/** The quick-access bar: what's in each slot (keys 1-9). */
export type Quickbar = (ItemId | null)[];

/**
 * The bar after the inventory changes from `before` to `after`, the way
 * Minecraft's works: a slot empties when you run out of its item, and a kind of
 * item you didn't have before goes into the first free slot (if there is one).
 */
export function syncQuickbar(bar: Quickbar, before: Inventory, after: Inventory): Quickbar {
  const next = bar.map((x) => (x && count(after, x) > 0 ? x : null));
  for (const id of ITEM_IDS) {
    if (count(after, id) === 0 || count(before, id) > 0 || next.includes(id)) continue;
    const free = next.indexOf(null);
    if (free < 0) break;
    next[free] = id;
  }
  return next;
}

/** Put an item in a slot (or empty it, with null). An item already elsewhere on the bar moves rather than appearing twice. */
export function assignSlot(bar: Quickbar, slot: number, item: ItemId | null): Quickbar {
  const next = item ? bar.map((x) => (x === item ? null : x)) : [...bar];
  next[slot] = item;
  return next;
}

/** Swap two slots (one dragged onto the other). */
export function swapSlots(bar: Quickbar, a: number, b: number): Quickbar {
  const next = [...bar];
  [next[a], next[b]] = [bar[b] ?? null, bar[a] ?? null];
  return next;
}

const QUICKBAR_KEY = "dyson.quickbar";
const QUICKBAR_SIZE = 9;

/** The quick-access bar as this browser last left it (it's a per-player convenience, so it lives here). */
export function loadQuickbar(): Quickbar {
  try {
    const saved: unknown = JSON.parse(localStorage.getItem(QUICKBAR_KEY) ?? "null");
    if (Array.isArray(saved)) return Array.from({ length: QUICKBAR_SIZE }, (_, i) => (isItemId(saved[i]) ? saved[i] : null));
  } catch {
    /* unreadable or blocked storage: start empty */
  }
  return Array.from({ length: QUICKBAR_SIZE }, () => null);
}

export function saveQuickbar(bar: Quickbar): void {
  try {
    localStorage.setItem(QUICKBAR_KEY, JSON.stringify(bar));
  } catch {
    /* storage blocked: the bar just won't be remembered */
  }
}
